import { VIENNA_MARKET_VALUE, isViennaMarket } from "./marketMeta";

export const CONDITION_FACTORS = {
  basic: 0.92,
  standard: 1.0,
  good: 1.06,
  premium: 1.12,
};

const FEATURE_FACTORS = {
  balcony: 1.02,
  lift: 1.01,
  furnished: 1.04,
};

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function average(values) {
  const nums = values.map(toNumber).filter((n) => n > 0);
  if (!nums.length) return 0;
  return nums.reduce((sum, n) => sum + n, 0) / nums.length;
}

export function sizeFactor(sizeM2) {
  const size = toNumber(sizeM2);
  if (size <= 0) return 0;
  if (size <= 30) return 1.10;
  if (size <= 45) return 1.05;
  if (size <= 75) return 1.00;
  if (size <= 100) return 0.97;
  return 0.94;
}

export function featureFactor(features = {}) {
  return Object.entries(FEATURE_FACTORS).reduce((factor, [key, boost]) => {
    return factor * (features[key] ? boost : 1);
  }, 1);
}

function benchmarkMarketsForSelection(markets, marketCode) {
  const code = String(marketCode || "").toUpperCase();
  if (code === VIENNA_MARKET_VALUE) {
    return markets.filter((m) => m.country === "AT" && isViennaMarket(m.code, m.name));
  }
  return markets.filter((m) => String(m.code || "").toUpperCase() === code);
}

export function estimateRent({
  markets = [],
  marketCode = VIENNA_MARKET_VALUE,
  sizeM2 = 0,
  condition = "standard",
  balcony = false,
  lift = false,
  furnished = false,
} = {}) {
  const benchmarkMarkets = benchmarkMarketsForSelection(markets, marketCode);
  const benchmarkPerSqm = average(benchmarkMarkets.map((m) => m.med_rent_sqm));
  const size = Math.max(0, toNumber(sizeM2));
  const sizeAdj = sizeFactor(size);
  const conditionAdj = CONDITION_FACTORS[condition] ?? 1;
  const featureAdj = featureFactor({ balcony, lift, furnished });
  const totalFactor = sizeAdj * conditionAdj * featureAdj;

  const estimatePerSqm = round2(benchmarkPerSqm * totalFactor);
  const benchmarkMonthly = Math.round(benchmarkPerSqm * size);
  const estimatedMonthly = Math.round(estimatePerSqm * size);
  const estimatedAnnual = Math.round(estimatedMonthly * 12);
  const deltaMonthly = estimatedMonthly - benchmarkMonthly;
  const deltaPct = benchmarkMonthly > 0 ? round2((deltaMonthly / benchmarkMonthly) * 100) : 0;

  return {
    benchmarkPerSqm: round2(benchmarkPerSqm),
    benchmarkMonthly,
    estimatedPerSqm: estimatePerSqm,
    estimatedMonthly,
    estimatedAnnual,
    deltaMonthly,
    deltaPct,
    sizeFactor: sizeAdj,
    conditionFactor: conditionAdj,
    featureFactor: featureAdj,
    totalFactor: round2(totalFactor),
    benchmarkCount: benchmarkMarkets.length,
    isViennaConsolidated: String(marketCode || "").toUpperCase() === VIENNA_MARKET_VALUE,
  };
}
